import {create} from 'zustand';
import {createJSONStorage, devtools, persist} from 'zustand/middleware';

import type {ContentPart} from '../store';
import {hasMeaningfulContent} from '../store';
import type {ComposerFile, FailedComposerImage} from './file';
import type {SelectedRun} from './run';
import {runKey} from './run';
import {createDebouncedDraftStorage} from './storage';

export interface Draft {
  body: ContentPart[];
  /**
   * failedFiles are files that failed to read; shown as error thumbnails, never
   * sent and never persisted.
   */
  failedFiles: FailedComposerImage[];
  files: ComposerFile[];
  /**
   * nonce is a sequence that's incremented each time the draft has been reset.
   */
  nonce: number;
  runs: SelectedRun[];
}

export interface DraftState {
  drafts: Record<string, Draft>;

  addFailedFiles: (key: string, failed: FailedComposerImage[]) => void;
  addFiles: (key: string, files: ComposerFile[]) => void;
  addRun: (key: string, run: SelectedRun) => void;
  removeFailedFile: (key: string, id: string) => void;
  removeFile: (key: string, id: string) => void;
  removeRun: (key: string, runIdentity: string) => void;
  /**
   * reset clears every field and bumps the `nonce` so consumers know to
   * rehydrate. Used on send; returns the draft to a blank slate.
   */
  reset: (key: string) => void;
  setBody: (key: string, body: ContentPart[]) => void;
}

export function hasPersistableContent(draft: Draft): boolean {
  return hasMeaningfulContent(draft.body) || draft.runs.length > 0;
}

/** draftKey maps a thread id (null for the new-chat view) to its draft key. */
export function draftKey(threadId: string | null): string {
  // NOTE: Prevent collisions between thread IDs and a new chat sentinel value.
  return threadId == null ? 'new-chat' : `thread:${threadId}`;
}

export function createDraftStore() {
  return create<DraftState>()(
    devtools(
      persist(
        set => ({
          drafts: {},
          addFailedFiles: (key, failed) =>
            set(s => ({
              drafts: updateDraft(s.drafts, key, d => ({
                ...d,
                failedFiles: [...d.failedFiles, ...failed],
              })),
            })),
          addFiles: (key, files) =>
            set(s => ({
              drafts: updateDraft(s.drafts, key, d => ({
                ...d,
                files: [...d.files, ...files],
              })),
            })),
          addRun: (key, run) =>
            set(s => ({
              drafts: updateDraft(s.drafts, key, d =>
                d.runs.some(r => runKey(r) === runKey(run))
                  ? d
                  : {...d, runs: [...d.runs, run]}
              ),
            })),
          removeFailedFile: (key, id) =>
            set(s => {
              const target = s.drafts[key]?.failedFiles.find(f => f.id === id);
              if (target) {
                URL.revokeObjectURL(target.previewUrl);
              }
              return {
                drafts: updateDraft(s.drafts, key, d => ({
                  ...d,
                  failedFiles: d.failedFiles.filter(f => f.id !== id),
                })),
              };
            }),
          removeFile: (key, id) =>
            set(s => ({
              drafts: updateDraft(s.drafts, key, d => ({
                ...d,
                files: d.files.filter(f => f.id !== id),
              })),
            })),
          removeRun: (key, runIdentity) =>
            set(s => ({
              drafts: updateDraft(s.drafts, key, d => ({
                ...d,
                runs: d.runs.filter(r => runKey(r) !== runIdentity),
              })),
            })),
          reset: key =>
            set(s => {
              revokeFailedFiles(s.drafts[key]);
              return {
                drafts: updateDraft(s.drafts, key, d => ({
                  ...emptyDraft(),
                  nonce: d.nonce + 1,
                })),
              };
            }),
          setBody: (key, body) =>
            set(s => ({
              drafts: updateDraft(s.drafts, key, d => ({...d, body})),
            })),
          // TODO: Add setDraftParts(key, parts) for programmatic prefill when a
          // caller lands. Split parts at the first text part into leading run
          // chips plus body, reuse reset's revoke + nonce bump to re-seed, and
          // gate overwrite of an occupied draft on an opts.force flag.
        }),
        {
          name: 'wb-agent-composer-drafts',
          version: 1,
          storage: createJSONStorage(createDebouncedDraftStorage),
          // NOTE: Only text and runs are persisted, and only for drafts that hold
          // meaningful content.
          partialize: state => ({
            drafts: Object.fromEntries(
              Object.entries(state.drafts)
                .filter(([, draft]) => hasPersistableContent(draft))
                .map(([key, draft]) => [
                  key,
                  {body: draft.body, runs: draft.runs},
                ])
            ),
          }),
          merge: (persisted, current) => {
            const saved =
              (persisted as {drafts?: Record<string, Partial<Draft>>})
                ?.drafts ?? {};
            const drafts: Record<string, Draft> = {};
            for (const [key, draft] of Object.entries(saved)) {
              drafts[key] = {
                ...emptyDraft(),
                body: draft.body ?? [],
                runs: draft.runs ?? [],
              };
            }
            return {...current, drafts};
          },
        }
      ),
      {name: 'DraftStore'}
    )
  );
}

export const useDraftStore = createDraftStore();

function emptyDraft(): Draft {
  return {body: [], runs: [], files: [], failedFiles: [], nonce: 0};
}

function revokeFailedFiles(draft: Draft | undefined): void {
  for (const failed of draft?.failedFiles ?? []) {
    URL.revokeObjectURL(failed.previewUrl);
  }
}

/**
 * updateDraft returns a new drafts map with `update` applied to the entry at
 * `key`, seeding an empty draft when the key is absent.
 */
function updateDraft(
  drafts: Record<string, Draft>,
  key: string,
  update: (draft: Draft) => Draft
): Record<string, Draft> {
  return {...drafts, [key]: update(drafts[key] ?? emptyDraft())};
}
